// Contrato compartido: lo valida la API con @cuorum/ai y lo consume la web (que solo puede importar ui y contracts).
// Paquete de evento que arma la IA a partir de la descripción del organizador. Es un BORRADOR: todo lo que el
// modelo no pudo inferir va vacío (null) y se lista en `review`, para que la pantalla lo marque. Nunca inventa
// fechas, lugares ni cupos.
import { z } from 'zod';

export const EVENT_TYPES = ['taller', 'charla', 'feria', 'capacitacion', 'consulta', 'otro'] as const;
export const STYLE_IDS = ['limpio', 'editorial', 'bloque', 'noche', 'institucional'] as const;

/** Orden en que la pantalla muestra el borrador construyéndose (y en que se pide al modelo). */
export const SECTIONS = ['event', 'agenda', 'registration', 'emails', 'live', 'style', 'review'] as const;
export type Section = (typeof SECTIONS)[number];

const localDateTime = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, 'fecha local AAAA-MM-DDTHH:mm');
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'hora HH:mm');
const text = (max: number) => z.string().trim().min(1).max(max);

const liveItem = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('poll'), prompt: text(200), options: z.array(text(80)).min(2).max(6) }),
  z.object({ kind: z.literal('wordcloud'), prompt: text(200) }),
  z.object({ kind: z.literal('questions'), prompt: text(200) }),
]);

export const EventPackageSchema = z.object({
  event: z.object({
    title: text(120),
    kicker: text(40).nullable(),
    summary: text(200),
    about: z.array(text(700)).min(1).max(3),
    type: z.enum(EVENT_TYPES),
    startsAt: localDateTime.nullable(),
    endsAt: localDateTime.nullable(),
    timeZone: z.string().min(3).max(40),
    venue: z.object({ name: text(120).nullable(), address: text(200).nullable() }),
    capacity: z.number().int().positive().max(100_000).nullable(),
  }),
  agenda: z.array(z.object({ time: hhmm, title: text(120), detail: text(160).nullable() })).max(8),
  registration: z.object({
    question: z.object({ label: text(120), options: z.array(text(60)).min(2).max(6) }).nullable(),
  }),
  emails: z.object({
    confirmation: z.object({ subject: text(120), intro: text(600) }),
    reminder: z.object({ subject: text(120), intro: text(600) }),
  }),
  live: z.object({ items: z.array(liveItem).max(3) }),
  style: z.object({ suggested: z.enum(STYLE_IDS), reason: text(200) }),
  review: z.array(z.object({ field: text(60), note: text(200) })).max(10),
}).strict();

export type EventPackage = z.infer<typeof EventPackageSchema>;

export type ValidationResult = { ok: true; value: EventPackage } | { ok: false; issues: string[] };

export function validatePackage(input: unknown): ValidationResult {
  const r = EventPackageSchema.safeParse(input);
  if (r.success) {
    const e = r.data.event;
    if (e.startsAt && e.endsAt && e.endsAt <= e.startsAt) return { ok: false, issues: ['event.endsAt: debe ser posterior a startsAt'] };
    return { ok: true, value: r.data };
  }
  return { ok: false, issues: r.error.issues.map((i) => `${i.path.join('.') || '(raíz)'}: ${i.message}`) };
}

/** Saca el primer objeto JSON de una respuesta (tolera ```json … ``` y texto antes o después). */
export function extractJson(raw: string): unknown {
  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  if (start < 0 || end <= start) throw new SyntaxError('no hay un objeto JSON en la respuesta');
  return JSON.parse(raw.slice(start, end + 1));
}
